import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { CrudEmptyState, CrudShellHeader, FilterBar, Loading } from "@/components/ds";
import { ClinicalPageShell } from "@/components/clinical";
import { MediaLibraryTour } from "@/components/ProductTour";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { apiGetJson, apiSendJson, clientRequestHeaders } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { optimizeImageFile } from "@/lib/image-optimize-client";
import { IMAGE_UPLOAD_PRESETS } from "@/lib/image-upload-presets";
import {
  ImagePlus,
  Loader2,
  Trash2,
  HardDrive,
  Link2,
  Download,
  Pencil,
  Tag,
  X,
} from "lucide-react";
import { toast } from "sonner";
import type { MediaAssetSummary } from "@/components/media/MediaPickerDialog";
import { useAppDialogs } from "@/components/ui/app-dialogs";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

export const Route = createFileRoute("/app/media")({
  component: MediaLibraryPage,
  head: () => ({ meta: [{ title: "Biblioteca de imágenes — Structura Market" }] }),
});

type MediaAssetFull = MediaAssetSummary & {
  tags: string[];
  title: string | null;
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function TagPill({ label, onRemove }: { label: string; onRemove?: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
      {label}
      {onRemove && (
        <button type="button" onClick={onRemove} className="hover:text-destructive">
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  );
}

function MediaLibraryPage() {
  const tid = useWorkspaceTenantId();
  const [items, setItems] = useState<MediaAssetFull[]>([]);
  const { confirm } = useAppDialogs();
  const [total, setTotal] = useState(0);
  const [usage, setUsage] = useState<{ assetCount: number; totalBytes: number } | null>(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [folder, setFolder] = useState("");
  const [folders, setFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    links: { entityType: string; entityName: string }[];
  } | null>(null);

  // Inline edit
  const [editTarget, setEditTarget] = useState<MediaAssetFull | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editTagInput, setEditTagInput] = useState("");
  const [editFolder, setEditFolder] = useState("");
  const [saving, setSaving] = useState(false);

  // Bulk tag
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkTagInput, setBulkTagInput] = useState("");
  const [bulkTagging, setBulkTagging] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    if (!tid) return;
    setLoading(true);
    const params = new URLSearchParams({ limit: "60" });
    if (q.trim()) params.set("q", q.trim());
    if (filter) params.set("filter", filter);
    if (folder) params.set("folder", folder);
    Promise.all([
      apiGetJson<{ items: MediaAssetFull[]; total: number }>(`/api/media-library?${params}`, tid),
      apiGetJson<{ usage: { assetCount: number; totalBytes: number } }>("/api/media-library?usage=1", tid),
      apiGetJson<{ folders: string[] }>("/api/media-library?folders=1", tid),
    ])
      .then(([list, u, f]) => {
        setItems(list.items ?? []);
        setTotal(list.total ?? 0);
        setUsage(u.usage ?? null);
        setFolders(f.folders ?? []);
      })
      .catch(() => toast.error("No se pudo cargar la biblioteca"))
      .finally(() => setLoading(false));
  }, [tid, q, filter, folder]);

  useEffect(() => {
    load();
  }, [load]);

  const onUploadFiles = async (files: FileList | null) => {
    if (!tid || !files?.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      const preset = IMAGE_UPLOAD_PRESETS.product;
      for (const file of Array.from(files)) {
        const opt = await optimizeImageFile(file, preset);
        form.append(
          "files",
          new File([opt.blob], file.name.replace(/\.[^.]+$/, `.${opt.ext}`), {
            type: opt.blob.type,
          }),
        );
        form.append("width", String(opt.width));
        form.append("height", String(opt.height));
      }
      const r = await fetch("/api/media-library", {
        method: "POST",
        credentials: "include",
        headers: clientRequestHeaders(tid),
        body: form,
      });
      const data = (await r.json()) as { uploaded?: unknown[]; errors?: string[] };
      if (data.errors?.length) toast.warning(data.errors.join("; "));
      if (data.uploaded?.length) toast.success(`${data.uploaded.length} imagen(es) subida(s)`);
      load();
    } catch {
      toast.error("Error al subir");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const requestDelete = async (id: string) => {
    if (!tid) return;
    const detail = await apiGetJson<{
      links: { entityType: string; entityName: string }[];
    }>(`/api/media-library?id=${id}`, tid);
    if (detail.links?.length) {
      setDeleteTarget({ id, links: detail.links });
      return;
    }
    await confirmDelete(id, false);
  };

  const confirmDelete = async (id: string, unlinkFirst: boolean) => {
    if (!tid) return;
    const url = `/api/media-library?id=${id}${unlinkFirst ? "&unlinkFirst=1" : ""}`;
    const r = await fetch(url, {
      method: "DELETE",
      credentials: "include",
      headers: clientRequestHeaders(tid),
    });
    if (!r.ok) {
      const err = (await r.json().catch(() => ({}))) as { error?: string };
      toast.error(err.error ?? "No se pudo eliminar");
      return;
    }
    toast.success("Imagen eliminada");
    setDeleteTarget(null);
    load();
  };

  const exportCsv = () => {
    const header = "id,filename,url,thumbnail_url,size_bytes,linked_count,tags\n";
    const rows = items
      .map(
        (i) =>
          `${i.id},${JSON.stringify(i.originalName)},${i.publicUrl},${i.thumbnailUrl ?? ""},${i.sizeBytes},${i.linkedEntitiesCount},${JSON.stringify((i.tags ?? []).join(";"))}`,
      )
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "media-library.csv";
    a.click();
  };

  const bulkDelete = async () => {
    if (!tid || !selected.size) return;
    if (!(await confirm({ title: `¿Eliminar ${selected.size} imagen(es)?`, description: "Esta acción no puede deshacerse.", destructive: true, confirmLabel: "Eliminar" }))) return;
    let deleted = 0;
    let errors = 0;
    for (const id of selected) {
      const r = await fetch(`/api/media-library?id=${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: clientRequestHeaders(tid),
      });
      if (r.ok) deleted++;
      else errors++;
    }
    toast[errors ? "warning" : "success"](
      errors
        ? `${deleted} eliminada(s), ${errors} con error (pueden estar en uso)`
        : `${deleted} imagen(es) eliminada(s)`,
    );
    setSelected(new Set());
    load();
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openEdit = (item: MediaAssetFull & { folder?: string | null }) => {
    setEditTarget(item);
    setEditTitle(item.title ?? "");
    setEditTags(item.tags ?? []);
    setEditTagInput("");
    setEditFolder(item.folder ?? "");
  };

  const addEditTag = () => {
    const t = editTagInput.trim().toLowerCase();
    if (t && !editTags.includes(t)) setEditTags((prev) => [...prev, t]);
    setEditTagInput("");
  };

  const saveEdit = async () => {
    if (!tid || !editTarget) return;
    setSaving(true);
    try {
      await apiSendJson(
        `/api/media-library`,
        "POST",
        { action: "patch", mediaId: editTarget.id, title: editTitle || null, tags: editTags, folder: editFolder.trim() || null },
        tid,
      );
      toast.success("Guardado");
      setEditTarget(null);
      load();
    } catch {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const doBulkTag = async () => {
    if (!tid || !selected.size) return;
    const tags = bulkTagInput
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    if (!tags.length) return;
    setBulkTagging(true);
    try {
      await apiSendJson(
        `/api/media-library`,
        "POST",
        { action: "tag", mediaIds: [...selected], tags },
        tid,
      );
      toast.success(`Tags agregados a ${selected.size} imagen(es)`);
      setBulkTagOpen(false);
      setBulkTagInput("");
      setSelected(new Set());
      load();
    } catch {
      toast.error("No se pudo aplicar tags");
    } finally {
      setBulkTagging(false);
    }
  };

  return (
    <div>
      <MediaLibraryTour />
      <CrudShellHeader
        title="Biblioteca de imágenes"
        description="Imágenes de tu catálogo y tu tienda"
        actions={
          <div className="flex flex-wrap gap-2">
            {selected.size > 0 && (
              <>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setBulkTagOpen(true)}>
                  <Tag className="h-4 w-4" /> Tag {selected.size}
                </Button>
                <Button variant="destructive" size="sm" className="gap-1.5" onClick={() => void bulkDelete()}>
                  <Trash2 className="h-4 w-4" /> Eliminar {selected.size}
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={exportCsv}>
              <Download className="h-4 w-4" /> CSV
            </Button>
            <Button size="sm" className="gap-1.5" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              {uploading ? "Subiendo…" : "Subir imágenes"}
            </Button>
          </div>
        }
      />
      <ClinicalPageShell offset="fixed-title">
        <MerchantExperienceBar screenId="media" />
        <div className="flex flex-wrap items-center gap-3 mb-6">
        {usage && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground rounded-lg border border-border px-3 py-2">
            <HardDrive className="h-4 w-4" />
            {usage.assetCount} assets · {formatBytes(usage.totalBytes)}
          </div>
        )}
        <FilterBar
          search={{
            value: q,
            onChange: setQ,
            placeholder: "Buscar por nombre o tag…",
          }}
          className="flex-1"
        >
          <select
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="">Todas ({total})</option>
            <option value="orphan">Sin uso</option>
            <option value="product">Productos</option>
            <option value="category">Categorías</option>
            <option value="brand">Marcas</option>
          </select>
          {folders.length > 0 && (
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
            >
              <option value="">Todas las carpetas</option>
              {folders.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          )}
        </FilterBar>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => void onUploadFiles(e.target.files)}
        />
      </div>

      <div
        className="mb-6 rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void onUploadFiles(e.dataTransfer.files);
        }}
      >
        <p className="text-sm text-muted-foreground">
          Arrastrá imágenes aquí para subida múltiple
        </p>
      </div>

      {loading ? (
        <Loading variant="list" rows={4} />
      ) : items.length === 0 ? (
        <CrudEmptyState
          icon={<ImagePlus className="h-8 w-8" />}
          title="Todavía no subiste imágenes"
          description="Subí la primera o arrastrala al área de arriba."
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              className={`group relative rounded-xl border overflow-hidden bg-card ${
                selected.has(item.id) ? "ring-2 ring-primary" : "border-border"
              }`}
            >
              <button
                type="button"
                className="absolute top-2 left-2 z-10 h-5 w-5 rounded border bg-background/90"
                onClick={() => toggleSelect(item.id)}
                aria-label="Seleccionar"
              >
                {selected.has(item.id) && <span className="block h-full w-full bg-primary rounded-sm scale-75" />}
              </button>
              <img
                src={item.thumbnailUrl ?? item.publicUrl}
                alt={item.title ?? item.originalName}
                className="aspect-square w-full object-cover"
                loading="lazy"
              />
              <div className="p-2 space-y-1">
                <p className="text-xs font-medium truncate">{item.title || item.originalName}</p>
                <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                  {item.width && item.height ? `${item.width}×${item.height} · ` : ""}
                  {formatBytes(item.sizeBytes)}
                  {item.linkedEntitiesCount > 0 && (
                    <>
                      {" · "}
                      <Link2 className="h-3 w-3 inline" />
                      {item.linkedEntitiesCount}
                    </>
                  )}
                </p>
                {(item.tags ?? []).length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {item.tags.slice(0, 3).map((t) => (
                      <TagPill key={t} label={t} />
                    ))}
                    {item.tags.length > 3 && (
                      <span className="text-[10px] text-muted-foreground">+{item.tags.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="h-8 w-8"
                  onClick={() => openEdit(item)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  className="h-8 w-8"
                  onClick={() => void requestDelete(item.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Inline edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Editar imagen</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-4">
              <img
                src={editTarget.thumbnailUrl ?? editTarget.publicUrl}
                alt={editTarget.originalName}
                className="w-full rounded-lg object-cover aspect-video"
              />
              <div className="space-y-2">
                <label className="text-sm font-medium">Título</label>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder={editTarget.originalName}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Carpeta</label>
                <Input
                  value={editFolder}
                  onChange={(e) => setEditFolder(e.target.value)}
                  placeholder="ej: productos, banners, logos"
                  list="folder-suggestions"
                />
                {folders.length > 0 && (
                  <datalist id="folder-suggestions">
                    {folders.map((f) => <option key={f} value={f} />)}
                  </datalist>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Tags</label>
                <div className="flex gap-2">
                  <Input
                    value={editTagInput}
                    onChange={(e) => setEditTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addEditTag();
                      }
                    }}
                    placeholder="Agregar tag y Enter"
                    className="flex-1"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={addEditTag}>
                    +
                  </Button>
                </div>
                {editTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {editTags.map((t) => (
                      <TagPill
                        key={t}
                        label={t}
                        onRemove={() => setEditTags((prev) => prev.filter((x) => x !== t))}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void saveEdit()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk tag dialog */}
      <Dialog open={bulkTagOpen} onOpenChange={(o) => !o && setBulkTagOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Agregar tags a {selected.size} imagen(es)</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Tags (separados por coma)</label>
            <Input
              value={bulkTagInput}
              onChange={(e) => setBulkTagInput(e.target.value)}
              placeholder="ej: verano, promo, destacado"
              onKeyDown={(e) => e.key === "Enter" && void doBulkTag()}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkTagOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void doBulkTag()} disabled={bulkTagging || !bulkTagInput.trim()}>
              {bulkTagging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar imagen?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {deleteTarget?.links.length ? (
                  <>
                    <p>Esta imagen está asociada a:</p>
                    <ul className="list-disc pl-5">
                      {deleteTarget.links.map((l, i) => (
                        <li key={i}>
                          {l.entityType}: {l.entityName}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p>Se borrará de R2 y de la biblioteca.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            {deleteTarget?.links.length ? (
              <AlertDialogAction
                onClick={() => void confirmDelete(deleteTarget.id, true)}
              >
                Desasociar y eliminar
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                onClick={() => deleteTarget && void confirmDelete(deleteTarget.id, false)}
              >
                Eliminar
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClinicalPageShell>
    </div>
  );
}
